/**
 * The flight map's own look (src/components/flight/FlightMap.jsx): the app's
 * dark basemap style (src/utils/basemap.js) recoloured into a deep-navy
 * chart. Land is navy and the sea near-black, coastlines glow cyan over a
 * faint latitude/longitude grid, and borders and country names sit back in
 * muted blue. Roads, buildings, parks, land cover and airport grounds are
 * hidden: a route across the country needs coastlines, borders and a few
 * names, not streets. City and state names are hidden too: the airports
 * carry their own labels, and a city's name would sit on its airport's.
 *
 * Layers are matched by the OpenMapTiles source layer they draw, not by id,
 * so this holds for OpenFreeMap's dark style and for any other
 * OpenMapTiles-schema style VITE_BASEMAP_STYLE_URL points at. A layer it
 * doesn't know is hidden. The Stays, /explore and Analytics maps keep the
 * style as it is.
 */

export const FLIGHT_MAP_COLORS = Object.freeze({
  land: '#0b1428',
  sea: '#040810',
  coast: '#38bdf8',
  grid: '#38bdf8',
  border: 'rgba(96,165,250,0.32)',
  stateBorder: 'rgba(96,165,250,0.14)',
  label: 'rgba(186,206,240,0.5)',
  seaLabel: 'rgba(56,189,248,0.3)',
  halo: 'rgba(4,8,16,0.85)',
});

const GRID_SOURCE = 'flight-map-grid';

// Each grid line is drawn from the zoom where its spacing is wide enough to
// read: every 20° from the start, 10° from zoom 2, 5° from 3 and 1° from 5.
const GRID_STEPS = [
  { step: 20, minzoom: 0, opacity: 0.09 },
  { step: 10, minzoom: 2, opacity: 0.07 },
  { step: 5, minzoom: 3, opacity: 0.06 },
  { step: 1, minzoom: 5, opacity: 0.05 },
];

/** Lines of longitude and latitude every degree, each tagged with the coarsest grid it belongs to. */
export function graticule() {
  const stepOf = (deg) => GRID_STEPS.find(({ step }) => Math.abs(deg) % step === 0).step;
  const line = (deg, coordinates) => ({ type: 'Feature', properties: { step: stepOf(deg) }, geometry: { type: 'LineString', coordinates } });
  const features = [];
  for (let lon = -180; lon <= 180; lon += 1) features.push(line(lon, [[lon, -85], [lon, 85]]));
  for (let lat = -80; lat <= 80; lat += 1) features.push(line(lat, [[-180, lat], [180, lat]]));
  return { type: 'FeatureCollection', features };
}

const gridLayers = () => GRID_STEPS.map(({ step, minzoom, opacity }) => ({
  id: `flight-map-grid-${step}`,
  type: 'line',
  source: GRID_SOURCE,
  ...(minzoom ? { minzoom } : {}),
  filter: ['==', ['get', 'step'], step],
  paint: { 'line-color': FLIGHT_MAP_COLORS.grid, 'line-opacity': opacity, 'line-width': 0.8 },
}));

// Lakes and reservoirs get a dimmer outline than the sea, or a zoomed-in map
// is covered in small cyan specks.
const byWaterClass = (sea, lake) => ['match', ['get', 'class'], 'ocean', sea, lake];

/** The coastline: a soft cyan glow under a thin bright line, traced around the sea. */
function coastLayers(water) {
  const base = {
    type: 'line',
    source: water.source,
    'source-layer': 'water',
    ...(water.filter ? { filter: water.filter } : {}),
    layout: { 'line-join': 'round' },
  };
  return [
    {
      ...base,
      id: 'flight-map-coast-glow',
      paint: {
        'line-color': FLIGHT_MAP_COLORS.coast,
        'line-opacity': byWaterClass(0.16, 0.05),
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 2, 6, 6],
        'line-blur': ['interpolate', ['linear'], ['zoom'], 0, 2, 6, 5],
      },
    },
    {
      ...base,
      id: 'flight-map-coast',
      paint: {
        'line-color': FLIGHT_MAP_COLORS.coast,
        'line-opacity': byWaterClass(0.55, 0.28),
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 6, 1.1],
      },
    },
  ];
}

const hidden = (layer) => ({ ...layer, layout: { ...layer.layout, visibility: 'none' } });

// The place labels kept: the layers whose filter picks out countries (or
// continents), in either filter syntax.
const COUNTRY_NAMES = /"(country|continent)"/;

function recolor(layer) {
  const sourceLayer = layer['source-layer'];
  if (layer.type === 'background') {
    return { ...layer, paint: { ...layer.paint, 'background-color': FLIGHT_MAP_COLORS.land } };
  }
  if (sourceLayer === 'water' && layer.type === 'fill') {
    const paint = { ...layer.paint, 'fill-color': FLIGHT_MAP_COLORS.sea, 'fill-opacity': 1 };
    delete paint['fill-pattern']; // a pattern would paint over the colour
    return { ...layer, paint };
  }
  if (sourceLayer === 'waterway' && layer.type === 'line') {
    return { ...layer, paint: { ...layer.paint, 'line-color': FLIGHT_MAP_COLORS.sea, 'line-opacity': 0.9 } };
  }
  if (sourceLayer === 'boundary' && layer.type === 'line') {
    return {
      ...layer,
      paint: {
        ...layer.paint,
        'line-color': ['match', ['get', 'admin_level'], 2, FLIGHT_MAP_COLORS.border, FLIGHT_MAP_COLORS.stateBorder],
        'line-opacity': 1,
        'line-blur': 0,
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.6, 6, 1.1],
      },
    };
  }
  const countryNames = sourceLayer === 'place' && COUNTRY_NAMES.test(JSON.stringify(layer.filter ?? null));
  if ((countryNames || sourceLayer === 'water_name') && layer.type === 'symbol') {
    return {
      ...layer,
      layout: { ...layer.layout, 'text-letter-spacing': 0.14 },
      paint: {
        ...layer.paint,
        'text-color': countryNames ? FLIGHT_MAP_COLORS.label : FLIGHT_MAP_COLORS.seaLabel,
        'text-halo-color': FLIGHT_MAP_COLORS.halo,
        'text-halo-width': 1,
        'text-halo-blur': 0.5,
      },
    };
  }
  return hidden(layer);
}

/** The style JSON with the flight map's look. The style passed in is left as it was. */
export function flightMapStyle(style) {
  const layers = (style?.layers || []).map(recolor);
  if (!layers.some((l) => l.type === 'background')) {
    layers.unshift({ id: 'flight-map-land', type: 'background', paint: { 'background-color': FLIGHT_MAP_COLORS.land } });
  }
  // The grid and the coastline go just above the sea, under borders and names.
  let waterAt = -1;
  layers.forEach((l, i) => { if (l['source-layer'] === 'water' && l.type === 'fill') waterAt = i; });
  const water = waterAt >= 0 ? layers[waterAt] : null;
  const insertAt = water ? waterAt + 1 : layers.findIndex((l) => l.type === 'background') + 1;
  layers.splice(insertAt, 0, ...gridLayers(), ...(water ? coastLayers(water) : []));
  return {
    ...style,
    sources: { ...style?.sources, [GRID_SOURCE]: { type: 'geojson', data: graticule() } },
    layers,
  };
}
